import type { CSSProperties } from 'react';
import '../components/atomic/layout/app_screen/app_screen.css';
import { containFitScale } from '../../../infrastructure/runtime';
import { useAppFit } from '../../hooks/useAppFit';
import { Arrows } from '../components/atomic/buttons/arrows/Arrows';
import { SunBeams } from '../components/atomic/atmosphere/sun_beams/SunBeams';
import { InfoPopupButton } from '../components/atomic/buttons/info_popup_button/InfoPopupButton';
import { InfoPopupFrame2 } from '../components/atomic/dialog/info_popup_frame_2/InfoPopupFrame2';
import { ScreenHeader } from '../components/atomic/layout/screen_header/ScreenHeader';
import { NavBar } from '../components/composite/nav/nav_bar/NavBar';
import { SkyAtmosphere } from '../components/composite/atmosphere/sky_atmosphere/SkyAtmosphere';
import bodyTextSvg from './info_popup/body_text.svg';
import './info_popup/StickersInfoPopup.css';
import titleTextSvg from './info_popup/title_text.svg';
import {
  MAX_STICKER_PAGE,
  STICKERS_ARROW_POSITIONS,
  STICKERS_ARROW_SCALE,
  type StickersScreenModel,
} from './StickersScreenModelScreen';
import { StickersGrid } from './stickers_grid/StickersGrid';
import type { StickersScreenController } from './StickersScreenController';
import { HeaderText } from './texts/HeaderText';
import './stickers_screen.css';
import type { ScreenViewProps } from '../shared/screenViewProps';
import { useSwipeNavigate } from '../shared/gestures/useSwipeNavigate';

export type StickersScreenViewProps = ScreenViewProps<
  StickersScreenModel,
  StickersScreenController
>;

/** Stickers and rewards screen (Figma stickers 160:16959). */
export function StickersScreenView({ model, controller }: StickersScreenViewProps) {
  const swipe = useSwipeNavigate({
    axis: 'vertical',
    onSwipe: controller.handleNavigate,
    enabled: !model.infoOpen,
  });
  const fit = useAppFit();
  const contain = containFitScale(fit.scale);

  return (
    <div
      className="app-screen stickers-screen"
      style={swipe.style}
      onPointerDown={swipe.onPointerDown}
      onPointerUp={swipe.onPointerUp}
      onPointerCancel={swipe.onPointerCancel}
      onClickCapture={swipe.onClickCapture}
    >
      <SkyAtmosphere variant="welcome" />
      <SunBeams />
      <div
        className="stickers-plaques"
        style={
          {
            '--fit-contain-inv-x': String(contain.invX),
            '--fit-contain-inv-y': String(contain.invY),
            '--fit-contain-uniform': String(contain.uniform),
          } as CSSProperties
        }
      >
        <StickersGrid
          page={model.page}
          isStickerUnlocked={model.isStickerUnlocked}
        />
        <ScreenHeader showSubHeader={false} />
        <HeaderText />
        <Arrows
          directions={['up', 'down']}
          positions={STICKERS_ARROW_POSITIONS}
          scale={STICKERS_ARROW_SCALE}
          canNavigate={{
            up: model.page > 0,
            down: model.page < MAX_STICKER_PAGE,
          }}
          onNavigate={controller.handleNavigate}
        />
        <InfoPopupButton
          className={model.infoOpen ? 'info-popup-button--above-popup' : undefined}
          left={919}
          top={162}
          scale={1.503672}
          onClick={controller.handleInfoButtonClick}
        />
        {model.infoOpen ? (
          <InfoPopupFrame2
            titleSrc={titleTextSvg}
            bodySrc={bodyTextSvg}
            titleClassName="stickers-info-popup__title"
            bodyClassName="stickers-info-popup__body"
            titleAlt="Cloudy keeps track of everything!"
            bodyAlt="Complete a delivery successfully to get a new sticker reward. Collect the full set!"
            onClose={controller.handleInfoClose}
          />
        ) : null}
      </div>
      <NavBar
        activeTab={model.activeTab}
        onTabChange={controller.handleTabChange}
      />
    </div>
  );
}
